import React, { useEffect, useState } from 'react';
import DataTable from 'react-data-table-component';
import { Link } from 'react-router-dom';
import { Alert, Form, Row, Col, Button } from 'react-bootstrap';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import emsLogo from '../img/ems.png';

const removeDiacritics = (text) =>
    text.normalize("NFD").replace(/[\u0300-\u036f]/g, "");

const downloadPDF = (data) => {
    const doc = new jsPDF();

    doc.addImage(emsLogo, 'PNG', 80, 10, 50, 50);
    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    doc.text(removeDiacritics('ZÁZNAM O VÝJEZDU - EMS'), 20, 70);

    const fields = [
        ['Zpravu vydal', removeDiacritics(data.user)],
        ['Osetrujici', data.members && data.members !== "null" ? removeDiacritics(`${data.user}, ${data.members}`) : removeDiacritics(data.user)],
        ['Pacient', removeDiacritics(data.pacient)],
        ['Typ zasahu', removeDiacritics(data.typ)],
        ['Lokalita', removeDiacritics(data.location)],
        ['Datum', removeDiacritics(formatDate(data.dt))],
    ];

    autoTable(doc, {
        startY: 80,
        headStyles: { fillColor: [220, 53, 69] },
        body: fields,
        styles: { font: 'helvetica', fontSize: 12 },
        margin: { left: 20, right: 20 },
        theme: 'grid',
        columnStyles: {
            0: { fontStyle: 'bold', cellWidth: 50 },
            1: { cellWidth: 'auto' }
        }
    });

    const afterTableY = doc.lastAutoTable.finalY + 10;

    doc.setFont('helvetica', 'bold');
    doc.text(removeDiacritics('Popis zasahu:'), 20, afterTableY);

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(11);

    const cleanedDetails = removeDiacritics(data.details || '—');
    doc.text(doc.splitTextToSize(cleanedDetails, 170), 20, afterTableY + 8);

    doc.save(`ems_zaznam_${removeDiacritics(data.id || 'neznamy')}.pdf`);
};



const leaders = [
    '1357797105955242075',
    '1357797133100777643',
    '1357797196371595435',
    '1357797197697122465',
    '1357832069388177478'
];

const formatDate = (iso) => {
    const dt = new Date(iso);
    return dt.toLocaleString('cs-CZ', {
        weekday: 'short',
        day: 'numeric',
        month: 'numeric',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    });
};

const customStyles = {
    headCells: {
        style: {
            fontWeight: 'bold',
            fontSize: '16px',
        },
    },
};

const RecordsList = () => {
    const [records, setRecords] = useState([]);
    const [filtered, setFiltered] = useState([]);
    const [textFilter, setTextFilter] = useState('');
    const [dateFilter, setDateFilter] = useState('');
    const [isAllowed, setIsAllowed] = useState(false);

    useEffect(() => {
        const raw = localStorage.getItem('ems_member');
        if (raw) {
            const parsed = JSON.parse(raw);
            const roles = parsed.user.roles;

            const isLeader = roles.some(role => leaders.includes(role));
            setIsAllowed(isLeader);

            if (isLeader) {
                fetch('https://ems.lexikonn.eu/api/get_records.php')
                    .then(res => res.json())
                    .then(data => {
                        setRecords(data);
                        setFiltered(data);
                    });
            }
        }
    }, []);

    useEffect(() => {
        const text = textFilter.toLowerCase();
        const date = dateFilter.toLowerCase();

        const filteredData = records.filter(record => {
            const combined = [
                record.user,
                record.dcuser,
                record.members,
                record.pacient,
                record.typ,
                record.location
            ]
                .join(' ')
                .toLowerCase();

            const formattedDate = formatDate(record.dt).toLowerCase();

            return combined.includes(text) && formattedDate.includes(date);
        });

        setFiltered(filteredData);
    }, [textFilter, dateFilter, records]);

    if (!isAllowed) {
        return <Alert variant="danger" className="m-5 text-center lead">Přístup pouze pro vedení EMS!</Alert>;
    }

    const columns = [
        { name: 'Zapsal', selector: row => row.user, sortable: true },
        { name: 'Discord', selector: row => row.dcuser, sortable: true },
        { name: 'Zasahovali', selector: row => row.members, sortable: true },
        { name: 'Pacient', selector: row => row.pacient, sortable: true },
        { name: 'Typ', selector: row => row.typ, sortable: true },
        { name: 'Lokalita', selector: row => row.location, sortable: true },
        { name: 'Datum', selector: row => formatDate(row.dt), sortable: true },
    ];

    const ExpandedComponent = ({ data }) => (
        <div className="p-3 bg-light border rounded">
            <strong>Popis zásahu:</strong>
            <br/>
            <div
                dangerouslySetInnerHTML={{
                    __html: (data.details || '').replace(/\n/g, '<br/>')
                }}
            />
            {data.members && (
                <>
                    <hr/>
                    <strong>Kolegové:</strong> {data.members}
                </>
            )}
            <div className="text-end">
                <Button size="sm" variant="outline-primary" onClick={() => downloadPDF(data)}>
                    📄 Stáhnout PDF
                </Button>
            </div>

        </div>
    );

    return (
        <div className="container py-5">
            <Link to="/" className="btn btn-secondary mb-3">Zpět</Link>
            <h2 className="mb-4">Seznam výjezdů</h2>

            <Row className="mb-3">
                <Col md={6}>
                    <Form.Control
                        type="text"
                        placeholder="🔍 Hledat jméno, typ, lokalitu, pacienta..."
                        value={textFilter}
                        onChange={(e) => setTextFilter(e.target.value)}
                    />
                </Col>
                <Col md={6}>
                    <Form.Control
                        type="text"
                        placeholder="📅 Hledat datum (např. 5. 4. 2025)"
                        value={dateFilter}
                        onChange={(e) => setDateFilter(e.target.value)}
                    />
                </Col>
            </Row>

            <DataTable
                columns={columns}
                data={filtered}
                pagination
                highlightOnHover
                striped
                expandableRows
                expandableRowsComponent={ExpandedComponent}
                customStyles={customStyles}
                defaultSortField="datum"
                conditionalRowStyles={[
                    {
                        when: row => row.typ === 'Úmrtí (CK)',
                        style: {
                            backgroundColor: '#f8d7da',
                            color: '#721c24',
                        },
                    }
                ]}
            />

        </div>
    );
};

export default RecordsList;
